/* ═══════════════════════════════════════════════════════════════
   NotificationBell — Sidebar bell icon with unread badge + dropdown
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';
import { UserPlus, MessageSquare, RefreshCw, ClockAlert, CircleCheck, Bell } from 'lucide-react';
import type { TaskNotification } from '../../shared/types';
import { TaskService } from '../services/taskService';
import { Icon } from './ui/Icon';
import { rem } from '../lib/rem';

interface NotificationBellProps {
  memberId: string;
  collapsed: boolean;
  onNavigateTask?: (taskId: string) => void;
}

// Lucide icons through the kit Icon (DESIGN.md §5 no-emoji-icons) — the row
// text already carries the meaning, so these are decorative (aria-hidden).
const TYPE_ICONS: Record<string, LucideIcon> = {
  assigned: UserPlus,
  comment: MessageSquare,
  status_change: RefreshCw,
  due_soon: ClockAlert,
  completed: CircleCheck,
};

function NotificationBell({ memberId, collapsed, onNavigateTask }: NotificationBellProps) {
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!memberId) return;
    try {
      // Copy before sorting — TaskService.listTaskNotifications reads
      // straight from cloudCache's query(), which returns the SAME array
      // reference it mutates in place on every save; sorting it directly
      // would both corrupt the shared cache order and hand React back an
      // identical reference (no re-render) on the next poll.
      const all: TaskNotification[] = [...(await TaskService.listTaskNotifications(memberId))];
      // Sort newest first
      all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setNotifications(all);
    } catch {
      /* ignore */
    }
  }, [memberId]);

  // Poll every 30s + load on mount
  useEffect(() => {
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const unread = notifications.filter((n) => !n.read);

  const markAllRead = async () => {
    if (unread.length === 0) return;
    try {
      for (const n of unread) {
        await TaskService.markTaskNotificationRead(n.id);
      }
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* ignore */
    }
  };

  const markOneRead = (n: TaskNotification) => {
    if (n.read) return;
    setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    TaskService.markTaskNotificationRead(n.id).catch(() => {
      /* ignore — the next poll reconciles */
    });
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell button */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: collapsed ? `${rem(10)} 0` : `${rem(10)} var(--space-4)`,
          justifyContent: collapsed ? 'center' : 'flex-start',
          fontSize: 'var(--font-size-base)',
          color: 'var(--st)',
          position: 'relative',
        }}
        title={`${unread.length} unread notification${unread.length !== 1 ? 's' : ''}`}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread.length > 0 && (
          <span
            style={{
              position: 'absolute',
              top: rem(6),
              right: collapsed ? 'var(--space-3)' : undefined,
              left: collapsed ? undefined : rem(28),
              minWidth: 'var(--space-4)',
              height: 'var(--space-4)',
              borderRadius: rem(8),
              background: 'var(--color-danger)',
              color: 'var(--color-text-inverse)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 var(--space-1)',
              lineHeight: 1,
            }}
          >
            {unread.length > 99 ? '99+' : unread.length}
          </span>
        )}
        {!collapsed && <span>Notifications</span>}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: collapsed ? rem(56) : 0,
            width: rem(320),
            maxHeight: rem(420),
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: rem(8),
            boxShadow: '0 8px 24px rgba(0,0,0,.15)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: `${rem(10)} ${rem(14)}`,
              borderBottom: '1px solid var(--bd)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--tx)' }}
            >
              Notifications{' '}
              {unread.length > 0 && (
                <span style={{ color: 'var(--tm)', fontWeight: 400 }}>
                  ({unread.length} unread)
                </span>
              )}
            </span>
            {unread.length > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'var(--ac)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: 'var(--space-5)',
                  textAlign: 'center',
                  color: 'var(--tm)',
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                }}
              >
                No notifications
              </div>
            ) : (
              notifications.slice(0, 50).map((n) => (
                <div
                  key={n.id}
                  onClick={() => {
                    markOneRead(n);
                    if (onNavigateTask) onNavigateTask(n.taskId);
                    setOpen(false);
                  }}
                  style={{
                    padding: `${rem(10)} ${rem(14)}`,
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--bd)',
                    background: n.read ? 'transparent' : 'rgba(var(--acr), 0.06)',
                    display: 'flex',
                    gap: rem(10),
                    alignItems: 'flex-start',
                    transition: 'background .15s',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--font-size-md)',
                      flexShrink: 0,
                      color: 'var(--tm)',
                      display: 'flex',
                      // eslint-disable-next-line sf/no-raw-px -- 1px, below the >4 conversion threshold
                      marginTop: 1,
                    }}
                  >
                    <Icon icon={TYPE_ICONS[n.type] || Bell} aria-hidden="true" />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                        color: 'var(--tx)',
                        lineHeight: 1.4,
                        fontWeight: n.read ? 400 : 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {n.message}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--tm)',
                        // eslint-disable-next-line sf/no-raw-px -- 3px, below the >4 conversion threshold
                        marginTop: 3,
                      }}
                    >
                      {formatTime(n.createdAt)}
                    </div>
                  </div>
                  {!n.read && (
                    <span
                      style={{
                        width: rem(7),
                        height: rem(7),
                        borderRadius: '50%',
                        background: 'var(--ac)',
                        flexShrink: 0,
                        marginTop: rem(5),
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { NotificationBell };
